import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFormatters } from '@/lib/hooks/useFormatters'
import { useMemberEntry, useMemberEntrySync } from '@/lib/hooks/useMemberEntry'
import { SUPPORT_EMAIL } from '@/lib/memberEntry'

/** Longer than this, the reason starts folded (the SuperAdmin may write up to 2,000 characters). */
const FOLD_AT = 280

/**
 * F-86, criterion 10: the leadership's alarm while the SuperAdmin keeps the
 * community suspended. Shown on every screen, never dismissible, only when
 * the server sent the reason (owner, admin, moderator, finance, SuperAdmin);
 * a member sees nothing. The reason is plain text: React escapes it.
 *
 * Also keeps the suspension current for the whole app (`useMemberEntrySync`).
 */
export function SuspensionBanner({ className }: { className?: string }) {
  useMemberEntrySync()
  const { t } = useTranslation('common')
  const { formatDateTime } = useFormatters()
  const { suspended, reason, suspendedAt } = useMemberEntry()
  const [expanded, setExpanded] = useState(false)

  if (!suspended || !reason) return null

  const long = reason.length > FOLD_AT
  return (
    <div
      role="alert"
      data-testid="member-entry-banner"
      className={cn('border-b-4 border-red-700 bg-red-600 px-4 py-4 text-white sm:px-6', className)}
    >
      <div className="mx-auto flex max-w-7xl gap-3">
        <ShieldAlert className="mt-0.5 h-6 w-6 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-base font-bold leading-snug sm:text-lg">{t('memberEntry.bannerTitle')}</p>
          <p className="text-sm text-red-50">{t('memberEntry.bannerBody')}</p>
          <div className="rounded-lg bg-white/95 p-3 text-sm text-red-900">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-700">
              {t('memberEntry.reasonLabel')}
              {suspendedAt && (
                <span className="block font-normal normal-case tracking-normal text-red-600 sm:ml-2 sm:inline">
                  {t('memberEntry.since', { date: formatDateTime(suspendedAt) })}
                </span>
              )}
            </p>
            <p className={cn('whitespace-pre-line break-words', long && !expanded && 'line-clamp-3')} data-testid="member-entry-banner-reason">
              {reason}
            </p>
            {long && (
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                className="mt-1 text-xs font-semibold text-red-700 underline underline-offset-2"
              >
                {expanded ? t('memberEntry.showLess') : t('memberEntry.showMore')}
              </button>
            )}
          </div>
          <p className="text-sm text-red-50">
            {t('memberEntry.support')}{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-white underline underline-offset-2">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
